export type RazorpaySuccess = {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
};

export type RazorpayCheckoutConfig = {
  keyId: string;
  orderId: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
};

type RazorpayInstance = {
  open: () => void;
  on: (event: string, handler: (response: { error?: { description?: string } }) => void) => void;
};

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => RazorpayInstance;
  }
}

let checkoutScript: Promise<void> | undefined;

function loadCheckoutScript() {
  if (window.Razorpay) return Promise.resolve();
  if (checkoutScript) return checkoutScript;
  checkoutScript = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Razorpay Checkout could not be loaded. Check your connection and try again."));
    document.head.appendChild(script);
  });
  return checkoutScript;
}

export async function openRazorpayCheckout(config: RazorpayCheckoutConfig): Promise<RazorpaySuccess | null> {
  await loadCheckoutScript();
  if (!window.Razorpay) throw new Error("Razorpay Checkout is unavailable.");

  return new Promise((resolve, reject) => {
    let completed = false;
    const checkout = new window.Razorpay!({
      key: config.keyId,
      order_id: config.orderId,
      amount: config.amount,
      currency: config.currency,
      name: config.name,
      description: config.description,
      handler: (response: RazorpaySuccess) => {
        completed = true;
        resolve(response);
      },
      modal: {
        ondismiss: () => {
          if (!completed) resolve(null);
        }
      },
      retry: { enabled: true },
      theme: { color: "#1f513f" }
    });
    checkout.on("payment.failed", (response) => {
      completed = true;
      reject(new Error(response.error?.description ?? "The payment did not complete. No report use was consumed."));
    });
    checkout.open();
  });
}
